import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Bookmark,
  Search as SearchIcon,
  User as UserIcon,
  ChevronDown,
} from "lucide-react";
import { HEADER, BACK_BUTTON } from "@/constants/testIds";
import { Logo } from "@/components/brand/Logo";
import { RefreshButton } from "@/components/shared/RefreshButton";
import { MarketSwitcher } from "@/components/shared/MarketSwitcher";
import { useAuth } from "@/context/AuthContext";
import brand from "@/config/brand";

// Route → page title (source of truth for the unified header).
const PAGE_TITLES = {
  "/opportunities": "الفرص",
  "/signals":       "إشارات السوق",
  "/knowledge":     "مكتبة المعرفة",
  "/saved":         "المحفوظات",
  "/about":         "حول",
  "/privacy":       "سياسة الخصوصية",
  "/terms":         "شروط الاستخدام",
  "/contact":       "تواصل معنا",
};

const PARENT_MAP = {
  "/opportunities": "/",
  "/knowledge":     "/",
  "/saved":         "/",
  "/about":         "/",
  "/privacy":       "/",
  "/terms":         "/",
  "/contact":       "/",
};

// Primary nav — same 5 destinations as BottomNav, same order.
const NAV_ITEMS = [
  { to: "/",              label: "الرئيسية" },
  { to: "/opportunities", label: "الفرص" },
  { to: "/signals",       label: "إشارات السوق" },
  { to: "/knowledge",     label: "مكتبة المعرفة" },
  { to: "/saved",         label: "المحفوظات" },
  { to: "/about",         label: "عن فرصالوجيا" },
];

const resolveTitle = (pathname) => {
  if (PAGE_TITLES[pathname]) return PAGE_TITLES[pathname];
  if (pathname.startsWith("/opportunities/")) return "تفاصيل الفرصة";
  if (pathname.startsWith("/knowledge/"))     return "قصّة معرفة";
  if (pathname.startsWith("/admin"))          return "لوحة الإدارة";
  return null;
};

const resolveParent = (pathname) => {
  if (pathname.startsWith("/opportunities/")) return "/opportunities";
  if (pathname.startsWith("/knowledge/"))     return "/knowledge";
  return PARENT_MAP[pathname] ?? "/";
};

/**
 * Unified navigation header — Fursa's executive command center.
 *
 * • Sticky with translucent backdrop that firms up on scroll.
 * • Home mode:      brand + primary nav + search + bookmarks + notifications + user slot.
 * • Internal mode:  back button + centered page title + trailing utility cluster.
 * • Height 64px on desktop, 56px on mobile — always constant per page for scroll stability.
 */
export const Header = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const location = useLocation();
  const isHome = pathname === "/";
  const isAdmin = pathname.startsWith("/admin");
  const title = resolveTitle(pathname);

  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleBack = () => {
    if (location.key && location.key !== "default") navigate(-1);
    else navigate(resolveParent(pathname));
  };

  const shellClasses = `sticky top-0 z-50 w-full transition-[background-color,border-color,box-shadow] duration-300 ${
    scrolled
      ? "border-b border-surface-200/80 bg-white/85 backdrop-blur-xl supports-[backdrop-filter]:bg-white/70"
      : "border-b border-transparent bg-white/60 backdrop-blur-md supports-[backdrop-filter]:bg-white/50"
  }`;

  if (isAdmin) {
    // Admin uses its own AdminLayout header — this shell only appears on public routes.
    return null;
  }

  return (
    <header data-testid={HEADER.root} className={shellClasses}>
      <div className="mx-auto flex h-16 max-w-7xl items-center gap-3 px-4 sm:h-20 sm:px-6 lg:px-8">
        {/* ── LEADING (RTL start = visual right) ── */}
        {isHome ? (
          <Link
            to="/"
            className="flex shrink-0 items-center gap-2.5 sm:gap-3 outline-none focus-visible:opacity-90"
            aria-label={`${brand.name} — الصفحة الرئيسية`}
          >
            <Logo
              variant="mark"
              size="sm"
              className="!h-14 sm:!h-16"
            />
            <div className="flex flex-col leading-none">
              <span className="text-lg sm:text-xl font-bold tracking-tight text-ink-900">
                فرصالوجيا
              </span>
              <span className="mt-1 text-[10px] sm:text-[11px] font-medium uppercase tracking-[0.22em] text-ink-500">
                FURSALOGIA
              </span>
            </div>
          </Link>
        ) : (
          <button
            type="button"
            onClick={handleBack}
            data-testid={BACK_BUTTON.root}
            aria-label="رجوع"
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-ink-700 transition-colors hover:bg-surface-100 hover:text-ink-900"
          >
            <ArrowRight className="h-5 w-5" strokeWidth={1.75} />
          </button>
        )}

        {/* ── CENTER ── */}
        {isHome ? (
          <nav
            aria-label="التنقّل الرئيسي"
            data-testid="header-nav"
            className="mx-auto hidden items-center gap-1 lg:flex"
          >
            {NAV_ITEMS.map((item) => {
              const active = pathname === item.to;
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  data-testid={`header-nav-${item.to.replace("/", "") || "home"}`}
                  className={`relative rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                    active
                      ? "text-intel-900 after:absolute after:inset-x-3 after:-bottom-[9px] after:h-[2px] after:rounded-full after:bg-intel-900"
                      : "text-ink-700 hover:bg-surface-100 hover:text-ink-900"
                  }`}
                  aria-current={active ? "page" : undefined}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>
        ) : (
          <h1
            data-testid="header-page-title"
            className="mx-auto truncate px-2 text-sm font-semibold tracking-tight text-ink-900 sm:text-base"
          >
            {title}
          </h1>
        )}

        {/* ── TRAILING (RTL end = visual left) ── */}
        <div className="flex shrink-0 items-center gap-1">
          {/* Market Switcher — Phase 5 default+user override. */}
          <MarketSwitcher />
          {/* Global refresh — reloads the current route's data (session preserved). */}
          <RefreshButton />
          {/* Search — icon-only shortcut that focuses the real search on /opportunities. */}
          <IconLink
            to="/opportunities?focus=search"
            label="بحث في الفرص"
            testId="header-search"
            icon={SearchIcon}
            active={false}
          />
          <IconLink
            to="/saved"
            label="المحفوظات"
            testId="header-bookmarks"
            icon={Bookmark}
            active={pathname === "/saved"}
          />

          {/* Public account entry point — never hidden. */}
          <AccountMenu />
        </div>
      </div>
    </header>
  );
};

/* ─── Small utility components ────────────────────────────────── */

const IconLink = ({ to, label, testId, icon: Icon, active }) => (
  <Link
    to={to}
    data-testid={testId}
    aria-label={label}
    className={`relative inline-flex h-10 w-10 items-center justify-center rounded-lg transition-colors ${
      active
        ? "bg-intel-50 text-intel-900"
        : "text-ink-500 hover:bg-surface-100 hover:text-ink-900"
    }`}
    aria-current={active ? "page" : undefined}
  >
    <Icon className="h-4 w-4" strokeWidth={1.75} />
  </Link>
);

/* Public account entry point.
   • Anonymous → Sign in / Sign up links (never hidden).
   • Authenticated → avatar + name + dropdown (profile · admin · logout).
   Admin route is only offered to admin/editor roles. */
const AccountMenu = () => {
  const { user, isLoading, isAdmin, isEditor, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const nav = useNavigate();

  useEffect(() => {
    const onClickAway = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClickAway);
    return () => document.removeEventListener("mousedown", onClickAway);
  }, []);

  if (isLoading) {
    return <div data-testid="header-account-loading" className="ms-1 h-9 w-9 animate-pulse rounded-full bg-surface-100" />;
  }

  if (!user) {
    return (
      <div className="ms-1 flex items-center gap-1">
        <Link to="/account/sign-in" data-testid="header-signin"
              className="hidden rounded-md px-3 py-2 text-xs font-medium text-ink-700 hover:bg-surface-100 sm:inline-flex">تسجيل الدخول</Link>
        <Link to="/account/sign-up" data-testid="header-signup"
              className="rounded-md bg-intel-800 px-3 py-2 text-xs font-medium text-white hover:bg-intel-700">إنشاء حساب</Link>
      </div>
    );
  }

  const initial = (user.full_name || user.email || "?").trim().charAt(0).toUpperCase();
  const doLogout = async () => { setOpen(false); await logout(); nav("/"); };

  return (
    <div ref={ref} className="relative ms-1">
      <button type="button" onClick={() => setOpen(v => !v)} data-testid="header-account-trigger"
              aria-haspopup="menu" aria-expanded={open}
              className="inline-flex items-center gap-1 rounded-full border border-surface-200 bg-white px-1 py-1 pe-2 text-xs font-medium text-ink-800 hover:border-intel-300">
        <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-intel-800 text-white">{initial}</span>
        <ChevronDown className="h-3 w-3 text-ink-500" strokeWidth={2} />
      </button>
      {open && (
        <div data-testid="header-account-menu" role="menu"
             className="absolute end-0 z-50 mt-2 w-56 overflow-hidden rounded-lg border border-surface-200 bg-white shadow-lg">
          <div className="border-b border-surface-100 px-4 py-3">
            <div className="text-xs font-medium text-ink-900 truncate">{user.full_name || user.email}</div>
            <div dir="ltr" className="mt-0.5 text-[11px] text-ink-500 truncate">{user.email}</div>
          </div>
          <MenuLink to="/profile"                testId="menu-profile"      onClick={() => setOpen(false)}>الملف الشخصي</MenuLink>
          <MenuLink to="/profile/change-password" testId="menu-change-pw"   onClick={() => setOpen(false)}>تغيير كلمة المرور</MenuLink>
          <MenuLink to="/saved"                  testId="menu-saved"        onClick={() => setOpen(false)}>المحفوظات</MenuLink>
          {(isAdmin || isEditor) && (
            <MenuLink to="/admin"                testId="menu-admin"        onClick={() => setOpen(false)}>لوحة الإدارة</MenuLink>
          )}
          <button type="button" data-testid="menu-logout" onClick={doLogout}
                  className="block w-full border-t border-surface-100 px-4 py-2.5 text-start text-xs font-medium text-red-700 hover:bg-red-50">
            تسجيل الخروج
          </button>
        </div>
      )}
    </div>
  );
};

const MenuLink = ({ to, testId, onClick, children }) => (
  <Link to={to} data-testid={testId} onClick={onClick}
        className="block px-4 py-2.5 text-xs text-ink-700 hover:bg-surface-50 hover:text-ink-900">
    {children}
  </Link>
);

export default Header;
